/**
 * DuplicateCampaignModal — create a brand-new, unlinked copy of a campaign
 * template (fresh template row at v1 + cloned nodes with fresh ids and
 * remapped pointers — see duplicateTemplate / remapCampaignNodes).
 *
 * Non-admins duplicate to themselves (personal copy). Real admins
 * (canImpersonate) also get an owner picker:
 *   - Myself            -> personal copy owned by the admin
 *   - Company           -> shared company template (a Company Automation
 *                          source keeps its hidden automation scope)
 *   - a specific LO     -> personal copy owned by that user, surfaced in
 *                          THEIR Available Campaigns
 *
 * Nobody is subscribed to the copy until they activate it. Mount this modal
 * conditionally ({target && <DuplicateCampaignModal isOpen ... />}) so the
 * CRM-user list is only fetched when it actually opens.
 *
 * @param {object}   props.template     - source template (camelCase, needs id/name)
 * @param {Function} props.onDuplicated - (createdTemplateCamel, { ownedByMe }) =>
 *                                        callers navigate to the builder when
 *                                        ownedByMe, otherwise refresh lists
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { FiCopy } from 'react-icons/fi';
import { useAuth } from '../../../contexts/AuthContext';
import { useImpersonation } from '../../../contexts/ImpersonationContext';
import { useCrmUsers } from '../../../hooks/useCrmUsers';
import { duplicateTemplate, transformTemplateToCamelCase } from '../../../api/supabase/automations';
import { isCompanyAutomation } from '../../../utils/campaignAccess';

export default function DuplicateCampaignModal({ isOpen, onClose, template, onDuplicated }) {
  const { currentUser } = useAuth();
  const { canImpersonate } = useImpersonation();
  const { users, loading: usersLoading, getUserName } = useCrmUsers();
  const toast = useToast();
  const initialFocusRef = useRef();

  const [name, setName] = useState('');
  // 'me' | 'company' | a specific user's uid
  const [owner, setOwner] = useState('me');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setName(template?.name ? `Copy of ${template.name}` : '');
    setOwner('me');
  }, [template?.id, template?.name]);

  const sourceIsAutomation = isCompanyAutomation(template);

  const handleConfirm = async () => {
    if (!template?.id) return;
    const isCompany = owner === 'company';
    const targetUid = owner === 'me' ? currentUser?.uid : owner;
    setBusy(true);
    try {
      const created = await duplicateTemplate(template.id, {
        name: name.trim() || `Copy of ${template.name}`,
        createdBy: isCompany ? currentUser?.uid : targetUid,
        ownershipType: isCompany ? 'company' : 'user',
      });
      const camel = transformTemplateToCamelCase(created);
      // Company copies are editable by (real) admins — and only admins can
      // pick "company" here. Personal copies belong to the picked uid.
      const ownedByMe = isCompany ? canImpersonate : targetUid === currentUser?.uid;

      toast({
        title: 'Campaign duplicated',
        description: isCompany
          ? `"${camel.name}" created as a company ${sourceIsAutomation ? 'automation' : 'template'}.`
          : ownedByMe
            ? `"${camel.name}" is yours — opening it in the builder.`
            : `"${camel.name}" created for ${getUserName(targetUid)} — it's in their Available Campaigns.`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      onDuplicated?.(camel, { ownedByMe });
      onClose();
    } catch (err) {
      console.error('[DuplicateCampaignModal] duplicate error:', err);
      toast({
        title: 'Duplicate failed',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} initialFocusRef={initialFocusRef} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Duplicate Campaign</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            <Text fontSize="sm" color="gray.500">
              Creates an unlinked copy of <b>{template?.name}</b> with all of its steps.
              Nobody is subscribed to the copy until it&apos;s activated.
            </Text>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Name</FormLabel>
              <Input
                ref={initialFocusRef}
                size="sm"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={`Copy of ${template?.name || 'campaign'}`}
              />
            </FormControl>

            {canImpersonate && (
              <FormControl>
                <FormLabel fontSize="sm">Owner</FormLabel>
                <Select size="sm" value={owner} onChange={(e) => setOwner(e.target.value)}>
                  <option value="me">Myself (personal copy)</option>
                  <option value="company">
                    {sourceIsAutomation ? 'Company (automation copy)' : 'Company (shared template)'}
                  </option>
                  <optgroup label={usersLoading ? 'Loading users…' : 'Specific user (personal copy)'}>
                    {users
                      .filter((u) => u.uid !== currentUser?.uid)
                      .map((u) => (
                        <option key={u.uid} value={u.uid}>
                          {u.name || u.email}
                        </option>
                      ))}
                  </optgroup>
                </Select>
                {sourceIsAutomation && owner !== 'company' && (
                  <Text fontSize="xs" color="orange.500" mt={1}>
                    A personal copy of an automation becomes a regular, visible campaign
                    that only runs on the owner&apos;s records.
                  </Text>
                )}
              </FormControl>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" size="sm" onClick={onClose} isDisabled={busy}>
            Cancel
          </Button>
          <Button
            leftIcon={<FiCopy />}
            colorScheme="blue"
            size="sm"
            ml={3}
            onClick={handleConfirm}
            isLoading={busy}
            isDisabled={!template?.id}
          >
            Duplicate
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
